import { io } from 'socket.io-client';
import { getActiveSubscription } from '@/data/session/session-globals';

export const WS_EVENT_TYPES = {
    THREADS_BY_DOCUMENT_ID: 'threadsByDocumentId',
    NEW_THREAD: 'newThread',
    DELETED_THREAD: 'deletedThread',
    NEW_COMMENT: 'newComment',
    DELETED_COMMENT: 'deletedComment',
    NEW_EMOJI_REACTION: 'newEmojiReaction',
    DELETED_EMOJI_REACTION: 'deletedEmojiReaction',
};

const DOCUMENT_ID_DELIMITER = '_:';

const validCommentKinds = [
    'dashboard',
    'replay',
    'saved clip',
    'idea',
    'feedback',
];

// Holds a map of documentIds and each callback registered to the documentId
const documentIdCallbacks = new Map();
let socket;

const isLocal = __PROCESS__.LOCAL_ENV;
const socketHostname = isLocal ? 'local.pendo.io:8888' : window.location.hostname;

// connect to server
export function connectToWebSocketServer ({ dispatch }) {
    if (socket) return;

    const serverUrl = `wss://${socketHostname}/in-app-commenting`;
    const opts = {
        path: '/ws2/socket.io',
        transports: ['websocket'],
        forceNew: false,
        autoConnect: false,
    };
    socket = io(serverUrl, opts);
    socket.connect();

    socket.on('connect', () => {
        dispatch('pendoCollaboration/init', null, { root: true });

        // Rejoin rooms on reconnection
        if (documentIdCallbacks.size > 0) {
            for (const documentId of documentIdCallbacks.keys()) {
                subscribeToDocument({ documentId });
            }
        }
    });

    socket.on('connect_error', () => {
        dispatch('pendoCollaboration/teardown', null, { root: true });
    });

    socket.on('disconnect', (reason) => {
        dispatch('pendoCollaboration/teardown', null, { root: true });
        // socket.io won't auto-reconnect for these reasons
        if (reason === 'io server disconnect' || reason === 'io client disconnect') {
            socket = null;
            documentIdCallbacks.clear();
        }
        // For transport errors/timeouts, socket.io reconnects automatically
        // and the 'connect' handler will rejoin rooms
    });

    socket.on('message', ({ type, documentId, data }) => {
        documentIdCallbacks.get(documentId)?.forEach((cb) => cb({ type, data }));
    });
}

export function subscribeToDocument ({ documentId, callback }) {
    if (!socket) return () => {};

    // Join the room if not already subscribed
    if (!documentIdCallbacks.has(documentId)) {
        documentIdCallbacks.set(documentId, new Set());
        const subId = getActiveSubscription()?.id;
        joinRoom({ documentId, subId });
    }

    if(callback) {
        documentIdCallbacks.get(documentId).add(callback);
    }

    // Return unsubscribe function
    return function unsubscribe () {
        const callbacks = documentIdCallbacks.get(documentId);
        if (!callbacks) return;
        callbacks.delete(callback);
        if (callbacks.size === 0) {
            documentIdCallbacks.delete(documentId);
            socket?.emit('leave', documentId);
        }
    };
}

function buildPermissionEntitiesField ({ documentId, widgetId }) {
    const [ kind, ...idParts ] = documentId.split(DOCUMENT_ID_DELIMITER);
    // handles the case where the delimiter appears in the id
    const id = idParts.join(DOCUMENT_ID_DELIMITER);
    const entity = {};
    entity[kind.toLowerCase()] = [id];
    if (widgetId) {
        entity.widget = [ widgetId ];
    }

    return entity;
}

export async function joinRoom ({ documentId }) {
    const subId = getActiveSubscription()?.id;

    socket.emit('join', { subId, documentId });
}

export async function createThread ({ documentId, targetElementId, textBody }) {
    if (!documentId) {
        console.error('Missing required field documentId');

        return;
    }
    const subId = getActiveSubscription()?.id;
    const entities = buildPermissionEntitiesField({ documentId, widgetId: targetElementId });
    await synchronousSocketCall(socket, 'createThread', { subId, documentId, targetElementId, textBody, entities });
}

export async function deleteThread ({ documentId, threadId }) {
    const subId = getActiveSubscription()?.id;
    await synchronousSocketCall(socket, 'deleteThread', { subId, documentId, threadId });
}

export async function addComment ({ documentId, textBody, threadId }) {
    const subId = getActiveSubscription()?.id;
    await synchronousSocketCall(socket, 'addComment', { subId, documentId, textBody, threadId });
}

export async function deleteComment ({ documentId, threadId, commentId }) {
    const subId = getActiveSubscription()?.id;
    await synchronousSocketCall(socket, 'deleteComment', { subId, documentId, threadId, commentId });
}

export async function addEmojiReaction ({ documentId, threadId, commentId, emojiCode }) {
    const subId = getActiveSubscription()?.id;
    await synchronousSocketCall(socket, 'addEmojiReaction', { subId, documentId, threadId, commentId, emojiCode });
}

export async function deleteEmojiReaction ({ documentId, threadId, commentId, emojiCode }) {
    const subId = getActiveSubscription()?.id;
    await synchronousSocketCall(socket, 'deleteEmojiReaction', { subId, documentId, threadId, commentId, emojiCode });
}

export async function synchronousSocketCall (socket, messageName, messageData = {}) {
    if(!socket) throw new Error('Socket was not provided or is not yet initialized');

    const response = await socket.timeout(10000).emitWithAck(messageName, messageData);

    if(response.isError) {
        throw response;
    }

    return response;
}

export function generateDocumentId ({ kind, id }) {
    if (!validCommentKinds.includes(kind)) {
        console.error(`Invalid kind ${kind} provided.`);
    }
    const prefix = kind.toUpperCase();

    return `${prefix}${DOCUMENT_ID_DELIMITER}${id}`;
}

